import './multi-date-picker.scss'

import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useEffect, useState } from 'react'
import { Calendar, DateObject } from 'react-multi-date-picker'

import { dateRangeContains } from './multi-date-picker-utils'

export type MultiDatePickerProps = {
  editableDates: Date[]
  onUserSelection: (userSelectedDates: Date[]) => void
  readonlyDates: Date[]
}

export default function MultiDatePicker({
  editableDates,
  onUserSelection,
  readonlyDates
}: MultiDatePickerProps) {
  const [values, setValues] = useState<Date[]>([])
  const today = new DateObject(new Date())
  useEffect(() => {
    setValues([...readonlyDates, ...editableDates])
  }, [editableDates, readonlyDates])

  const isPastDate = (userDate: DateObject) => {
    if (userDate.year < today.year) return true
    if (userDate.year === today.year) return userDate.dayOfYear < today.dayOfYear
    return false
  }

  const onDateChange = (dates: DateObject[]) => {
    const newlyEditedDates: DateObject[] = []
    dates.forEach(date => {
      const isWeekend = [0, 6].includes(date.weekDay.index)
      if (!isWeekend && !dateRangeContains(date, readonlyDates)) {
        newlyEditedDates.push(date)
      }
    })

    const userDates = newlyEditedDates.map(item => item.toDate())

    if (onUserSelection) {
      onUserSelection(userDates)
    }
  }

  // https://shahabyazdi.github.io/react-multi-date-picker/map-days/
  const mapDaysHighlighting2 = ({ date, today }: any) => {
    const dayStylingProps: any = {}
    const isWeekend = [0, 6].includes(date.weekDay.index)
    const isCurrentDay = date.year === today.year && date.dayOfYear === today.dayOfYear
    const isReadonlySelectedDate = dateRangeContains(date, readonlyDates)
    const isUserEditableSelectedDate = dateRangeContains(date, editableDates)
    const shouldDisable = isWeekend || isPastDate(date) || isReadonlySelectedDate

    if (isReadonlySelectedDate) {
      dayStylingProps.className = 'calendar-day-selected-readonly'
    } else if (isUserEditableSelectedDate) {
      dayStylingProps.className = 'calendar-day-selected-editable'
    } else {
      dayStylingProps.className = 'calendar-day-unselected'
      if (isCurrentDay) {
        dayStylingProps.className = `calendar-day-today ${dayStylingProps.className}`
      }
    }
    if (shouldDisable) {
      dayStylingProps.className = `calendar-day-disabled ${dayStylingProps.className}`
    }

    return dayStylingProps
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Calendar
        fullYear
        multiple
        currentDate={undefined}
        mapDays={mapDaysHighlighting2}
        value={values}
        weekStartDayIndex={1}
        onChange={onDateChange}
      />
    </LocalizationProvider>
  )
}
